// Declare gtag for Google Analytics
declare global {
  interface Window {
    gtag?: any;
  }
}

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, AlertCircle, Apple, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/theme-toggle";
import { MobileNav } from "@/components/mobile-nav";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { motion } from "framer-motion";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";

// Constants
// const SIGNUP_COOLDOWN_KEY = "last_signup_attempt";
// const SIGNUP_COOLDOWN_HOURS = 0;
// const GOOGLE_ADS_CONVERSION_ID = 'AW-16672097897/qNP1CI3zpcEaEOmM8I0-'; // 상수화
const REDIRECT_PATH = "/search"; // 리디렉션 경로 상수화


// Google Ads 가입 전환 이벤트 추적 함수 주석 처리
// const trackSignupConversion = () => {
//   if (typeof window !== 'undefined' && window.gtag) {
//     window.gtag('event', 'conversion', { 'send_to': GOOGLE_ADS_CONVERSION_ID });
//     console.log('Google Ads 가입 전환 이벤트 전송됨');
//   } else {
//     console.warn('Google Ads gtag not found, skipping conversion tracking.');
//   }
// };

export function AuthPage() {
  const [isLogin, setIsLogin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPhoneFlow, setShowPhoneFlow] = useState<boolean>(true);
  const [showSocialLogins, setShowSocialLogins] = useState<boolean>(false);
  const [phone, setPhone] = useState<string>('');
  const [otp, setOtp] = useState<string>('');
  const [isCodeSent, setIsCodeSent] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);

  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const returnTo = REDIRECT_PATH;

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const errorParam = queryParams.get("error");
    const errorDescParam = queryParams.get("error_description");
    if (errorParam || errorDescParam) {
      setError(decodeURIComponent(errorDescParam || errorParam || "OAuth 인증 중 오류 발생"));
      const cleanUrl = location.pathname;
      navigate(cleanUrl, { replace: true, state: location.state });
    }

    const checkSessionAndTrack = async () => {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error("세션 확인 오류:", sessionError);
        return;
      }
      if (sessionData?.session) {
        const user = sessionData.session.user;
        const createdAt = user.created_at ? new Date(user.created_at) : null;
        const lastSignInAt = user.last_sign_in_at ? new Date(user.last_sign_in_at) : null;
        if (createdAt && lastSignInAt) {
          const timeDiff = Math.abs(lastSignInAt.getTime() - createdAt.getTime());
          if (timeDiff < 5 * 60 * 1000) {
            console.log("신규 사용자 OAuth 로그인 감지됨 (전환 추적은 signup 시점에 수행)");
          }
        }
        console.log("세션 확인됨, 리디렉션:", returnTo);
        navigate(returnTo, { replace: true });
      }
    };
    checkSessionAndTrack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // checkSignupCooldown 함수 주석 처리
  // const checkSignupCooldown = (): { allowed: boolean; message?: string } => {
  //   const lastSignup = localStorage.getItem(SIGNUP_COOLDOWN_KEY);
  //   if (!lastSignup) return { allowed: true };
  //   const lastSignupTime = parseInt(lastSignup);
  //   const currentTime = Date.now();
  //   const hoursSinceLastSignup = (currentTime - lastSignupTime) / (1000 * 60 * 60);
  //   if (hoursSinceLastSignup < SIGNUP_COOLDOWN_HOURS) {
  //     const hoursRemaining = Math.ceil(SIGNUP_COOLDOWN_HOURS - hoursSinceLastSignup);
  //     return {
  //       allowed: false,
  //       message: `이 기기에서는 ${hoursRemaining}시간 후에 다시 회원가입이 가능합니다.`,
  //     };
  //   }
  //   return { allowed: true };
  // };

  const handleOAuthSignIn = async (provider: 'google' | 'apple'): Promise<void> => {
    setError(null);
    if (provider === 'google') setIsGoogleLoading(true);
    if (provider === 'apple') setIsAppleLoading(true);
    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${window.location.origin}${returnTo}`,
          ...(provider === 'google' && {
            queryParams: { access_type: 'offline', prompt: 'consent' }
          }),
        },
      });
      if (oauthError) throw oauthError;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error(`${provider} 로그인 오류:`, error);
      setError(message || `${provider} 로그인 중 오류가 발생했습니다`);
      if (provider === 'google') setIsGoogleLoading(false);
      if (provider === 'apple') setIsAppleLoading(false);
    }
  };

  function formatPhone(input: string): string {
    const digits: string = input.replace(/\D/g, '').slice(0, 11);
    const part1: string = digits.slice(0, 3);
    const part2: string = digits.slice(3, 7);
    const part3: string = digits.slice(7, 11);
    let formattedPhoneString: string = part1;
    if (digits.length > 3) formattedPhoneString += ' ' + part2;
    if (digits.length > 7) formattedPhoneString += ' ' + part3;
    return formattedPhoneString;
  }

  async function handleSendCode(): Promise<void> {
    setError(null);
    setIsLoading(true);
    try {
      let formattedE164Phone = phone.replace(/\s|-/g, "");
      if (formattedE164Phone.startsWith("0")) {
        formattedE164Phone = "+82" + formattedE164Phone.substring(1);
      } else if (!formattedE164Phone.startsWith("+")) {
        formattedE164Phone = "+82" + formattedE164Phone;
      }
      const { error: smsError } = await supabase.auth.signInWithOtp({ phone: formattedE164Phone });
      if (smsError) throw smsError;
      toast({ title: '인증 코드 전송됨', description: '입력하신 번호로 코드가 전송되었습니다.' });
      setIsCodeSent(true);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      setError(message || '코드 전송에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerifyCode(): Promise<void> {
    setError(null);
    setIsVerifying(true);
    try {
      let formattedE164Phone = phone.replace(/\s|-/g, "");
      if (formattedE164Phone.startsWith("0")) {
        formattedE164Phone = "+82" + formattedE164Phone.substring(1);
      } else if (!formattedE164Phone.startsWith("+")) {
        formattedE164Phone = "+82" + formattedE164Phone;
      }
      const { error: verifyError } = await supabase.auth.verifyOtp({ phone: formattedE164Phone, token: otp, type: 'sms' });
      if (verifyError) throw verifyError;
      toast({ title: '로그인 성공', description: '전화번호 인증이 완료되었습니다.' });
      navigate(REDIRECT_PATH, { replace: true });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      setError(message || '인증에 실패했습니다.');
    } finally {
      setIsVerifying(false);
    }
  }

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemAnimation = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };

  const logoAnimation = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { type: "spring", stiffness: 500, damping: 20, delay: 0.1 }
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-gradient-to-b from-background via-background/95 to-background/90">
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-[40rem] w-full">
        <div className="absolute -left-[10%] top-0 h-[30rem] w-[40%] rounded-full bg-primary/20 blur-[128px]" />
        <div className="absolute -right-[10%] top-0 h-[30rem] w-[40%] rounded-full bg-accent/20 blur-[128px]" />
      </div>
      <header className="container sticky top-0 z-50 flex items-center justify-between py-4 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <img
            src="https://storage.googleapis.com/glide-prod.appspot.com/uploads-v2/FYU7LsqDXg9yUwI6tiLP/pub/lXbB8YOdvvz5IcMbcvsM.png"
            alt="INSTA LAB AI Logo"
            className="h-8 w-8 object-contain"
          />
          <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">INSTA LAB AI</h1>
        </div>
        <ThemeToggle />
      </header>
      <main className="container relative z-10 flex flex-1 flex-col items-center justify-center px-4 py-12">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerAnimation}
          className="w-full max-w-md space-y-6"
        >
          <motion.div
            variants={logoAnimation}
            className="flex flex-col items-center justify-center"
          >
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 via-background to-accent/20 p-4 shadow-lg ring-1 ring-white/10 backdrop-blur-lg">
              <img
                src="https://storage.googleapis.com/glide-prod.appspot.com/uploads-v2/FYU7LsqDXg9yUwI6tiLP/pub/lXbB8YOdvvz5IcMbcvsM.png"
                alt="INSTA LAB AI Logo"
                className="h-12 w-12 object-contain drop-shadow-md"
              />
            </div>
            <motion.h1
              variants={itemAnimation}
              className="mt-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-center text-3xl font-bold tracking-tight text-transparent"
            >
              {isLogin ? "환영합니다" : "가입하기"}
            </motion.h1>
            <motion.p
              variants={itemAnimation}
              className="mt-2 text-center text-sm text-muted-foreground"
            >
              {isLogin
                ? "인스타그램 계정과 대화하는 AI 서비스를 시작하세요"
                : "새 계정을 만들어 인스타그램 방문자를 알아보세요."}
            </motion.p>
          </motion.div>
          <motion.div
            variants={itemAnimation}
            className="overflow-hidden rounded-2xl border border-white/20 bg-background/80 p-8 shadow-xl backdrop-blur-sm"
          >
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4"
              >
                <Alert variant="destructive" className="py-3 bg-destructive/10 border-destructive/30">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle className="text-sm font-medium">인증 오류</AlertTitle>
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              </motion.div>
            )}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4"
              >
                <Alert
                  className="py-3 bg-green-50/80 dark:bg-green-900/20 border-green-300 dark:border-green-800"
                >
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4">
                      <path d="M7.49991 0.877045C3.84222 0.877045 0.877075 3.84219 0.877075 7.49988C0.877075 11.1575 3.84222 14.1227 7.49991 14.1227C11.1576 14.1227 14.1227 11.1575 14.1227 7.49988C14.1227 3.84219 11.1576 0.877045 7.49991 0.877045ZM1.82708 7.49988C1.82708 4.36686 4.36689 1.82704 7.49991 1.82704C10.6329 1.82704 13.1727 4.36686 13.1727 7.49988C13.1727 10.6329 10.6329 13.1727 7.49991 13.1727C4.36689 13.1727 1.82708 10.6329 1.82708 7.49988ZM10.1589 5.53774C10.3178 5.31192 10.2636 5.00001 10.0378 4.84109C9.81194 4.68217 9.50004 4.73642 9.34112 4.96223L6.51977 8.97646L5.35681 7.78706C5.16334 7.59002 4.84677 7.58711 4.64973 7.78058C4.45268 7.97404 4.44978 8.29061 4.64325 8.48765L6.22658 10.1003C6.33054 10.2062 6.47617 10.2604 6.62407 10.2483C6.77197 10.2363 6.90686 10.1591 6.99226 10.0377L10.1589 5.53774Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                    </svg>
                    <AlertTitle className="text-sm font-medium">{success}</AlertTitle>
                  </div>
                </Alert>
              </motion.div>
            )}
            {!showPhoneFlow && showSocialLogins && (
              <motion.div
                className="w-full space-y-3 pt-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Button
                  onClick={() => handleOAuthSignIn('apple')}
                  disabled={isLoading || isGoogleLoading || isAppleLoading}
                  variant="outline"
                  className="relative flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-card px-4 py-2 text-sm font-medium shadow-sm transition-all hover:bg-muted focus:ring-2 focus:ring-primary/40"
                >
                  {isAppleLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Apple className="mr-2 h-5 w-5" />
                      <span>Apple로 계속하기</span>
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleOAuthSignIn('google')}
                  disabled={isLoading || isGoogleLoading || isAppleLoading}
                  variant="outline"
                  className="relative flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-card px-4 py-2 text-sm font-medium shadow-sm transition-all hover:bg-muted focus:ring-2 focus:ring-primary/40"
                >
                  {isGoogleLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                        <path d="M1 1h22v22H1z" fill="none" />
                      </svg>
                      <span>Google로 계속하기</span>
                    </>
                  )}
                </Button>
                <Button
                  variant="link"
                  className="w-full text-sm text-muted-foreground hover:text-primary pt-2"
                  onClick={() => {
                    setShowPhoneFlow(true);
                    setShowSocialLogins(false);
                    setIsLogin(false);
                    setError(null);
                    setSuccess(null);
                  }}
                  disabled={isLoading || isGoogleLoading || isAppleLoading}
                >
                  <Phone className="mr-2 h-4 w-4" />
                  전화번호로 돌아가기
                </Button>
              </motion.div>
            )}

            {showPhoneFlow && (
              <motion.form
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (isCodeSent) {
                    handleVerifyCode();
                  } else {
                    handleSendCode();
                  }
                }} className="space-y-6">
                <motion.div
                  key="phone-input-section"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="space-y-2"
                >
                  <Label htmlFor="phoneInputOTP" className="text-sm font-medium text-center block">휴대폰 번호를 입력해주세요</Label>
                  <InputOTP
                    id="phoneInputOTP"
                    maxLength={11}
                    value={phone.replace(/\D/g, '')}
                    onChange={(value: string) => setPhone(formatPhone(value))}
                    disabled={isLoading || isVerifying}
                    containerClassName="flex flex-col items-center space-y-2"
                  >
                    <div className="flex flex-col items-center w-full space-y-2">
                      <div className="relative flex items-center justify-center">
                        <InputOTPGroup className="flex justify-center">
                          <InputOTPSlot index={0} className="h-9 w-9 text-xs rounded-md border" />
                          <InputOTPSlot index={1} className="h-9 w-9 text-xs rounded-md border" />
                          <InputOTPSlot index={2} className="h-9 w-9 text-xs rounded-md border" />
                        </InputOTPGroup>
                        <span className="absolute left-full ml-1 text-sm text-muted-foreground transform -translate-y-1/2 top-1/2 pt-3.5">-</span>
                      </div>

                      <div className="flex items-center justify-center w-full space-x-0.5 pt-2">
                        <InputOTPGroup className="flex">
                          <InputOTPSlot index={3} className="h-9 w-9 text-xs rounded-md border" />
                          <InputOTPSlot index={4} className="h-9 w-9 text-xs rounded-md border" />
                          <InputOTPSlot index={5} className="h-9 w-9 text-xs rounded-md border" />
                          <InputOTPSlot index={6} className="h-9 w-9 text-xs rounded-md border" />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup className="flex">
                          <InputOTPSlot index={7} className="h-9 w-9 text-xs rounded-md border" />
                          <InputOTPSlot index={8} className="h-9 w-9 text-xs rounded-md border" />
                          <InputOTPSlot index={9} className="h-9 w-9 text-xs rounded-md border" />
                          <InputOTPSlot index={10} className="h-9 w-9 text-xs rounded-md border" />
                        </InputOTPGroup>
                      </div>
                    </div>
                  </InputOTP>
                </motion.div>

                {isCodeSent && (
                  <motion.div
                    key="otp-input-section"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className="space-y-2"
                  >
                    <Label htmlFor="otp" className="text-sm font-medium text-center block">인증 코드</Label>
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={(value: string) => setOtp(value)}
                      disabled={isVerifying}
                    >
                      <InputOTPGroup className="w-full flex justify-center">
                        <InputOTPSlot index={0} className="h-9 w-9 text-xs rounded-md border" />
                        <InputOTPSlot index={1} className="h-9 w-9 text-xs rounded-md border" />
                        <InputOTPSlot index={2} className="h-9 w-9 text-xs rounded-md border" />
                      </InputOTPGroup>
                      <InputOTPSeparator className="mx-0.5" />
                      <InputOTPGroup className="w-full flex justify-center">
                        <InputOTPSlot index={3} className="h-9 w-9 text-xs rounded-md border" />
                        <InputOTPSlot index={4} className="h-9 w-9 text-xs rounded-md border" />
                        <InputOTPSlot index={5} className="h-9 w-9 text-xs rounded-md border" />
                      </InputOTPGroup>
                    </InputOTP>
                  </motion.div>
                )}
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full rounded-xl shadow-lg transition-all duration-300 ease-in-out 
                    ${(isCodeSent ? otp.replace(/\D/g, '').length === 6 : phone.replace(/\D/g, '').length === 11) && !(isLoading || isVerifying)
                      ? 'bg-gradient-to-r from-primary to-accent shadow-primary/30 hover:shadow-primary/40'
                      : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'}`}
                >
                  <Button
                    type="submit"
                    className={`w-full h-12 text-lg font-semibold text-white 
                      ${(isCodeSent ? otp.replace(/\D/g, '').length === 6 : phone.replace(/\D/g, '').length === 11) && !(isLoading || isVerifying)
                        ? ''
                        : 'opacity-50'}`}
                    disabled={!(isCodeSent ? otp.replace(/\D/g, '').length === 6 : phone.replace(/\D/g, '').length === 11) || isLoading || isVerifying}
                  >
                    {isCodeSent
                      ? (isVerifying ? <div className="flex items-center justify-center"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> 확인 중...</div> : '인증번호 확인')
                      : (isLoading ? <div className="flex items-center justify-center"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> 전송 중...</div> : '인증번호 받기')}
                  </Button>
                </motion.div>

                <div className="flex justify-between items-center mt-3">
                  <Button
                    variant="link"
                    className="h-auto p-0 text-xs text-muted-foreground hover:text-primary/80"
                    onClick={() => {
                      setShowPhoneFlow(false);
                      setShowSocialLogins(true);
                      setIsLogin(true);
                      setError(null);
                      setSuccess(null);
                      setPhone('');
                      setOtp('');
                      setIsCodeSent(false);
                    }}
                    disabled={isLoading || isVerifying}
                  >
                    다른 방법으로 시작하기
                  </Button>
                  {isCodeSent && (
                    <Button variant="link" className="h-auto p-0 text-xs text-muted-foreground hover:text-primary/80" onClick={() => handleSendCode()} disabled={isLoading || isVerifying}>
                      {isLoading ? '전송 중...' : '인증번호 다시 받기'}
                    </Button>
                  )}
                </div>
              </motion.form>
            )}
            <motion.p
              variants={itemAnimation}
              className="text-xs text-center text-muted-foreground mt-4"
            >
              계속하면 <a href="/terms" className="text-primary hover:underline">이용약관</a> 및{" "}
              <a href="/terms" className="text-primary hover:underline">개인정보 정책</a>에 동의하게 됩니다.
            </motion.p>
            <motion.div
              variants={itemAnimation}
              className="text-center text-xs text-muted-foreground mt-2"
            >
              문제가 있으신가요? <a href="mailto:support@INSTA LAB.ai" className="text-primary hover:underline">고객지원</a>에 문의하세요
            </motion.div>
          </motion.div>
        </motion.div>
      </main>
      <MobileNav />
    </div>
  );
}